import { useState } from 'react';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useMutation } from '@apollo/client';
import { logger } from '../../utils/logging';
import { NewUserFormInput } from '../../interfaces/interfaces';
import { Button, Col, Form as BForm, Row } from 'react-bootstrap';
import TextField from '../text-field/text-field';
import { DropdownV2, OptionV2 } from '@spins-gcp/shared-components-react';
import styles from './new-user.module.scss';
import { NewUserWithoutAdminSubmitted } from './new-user-without-admin-submitted';
import { useAtomValue, useSetAtom } from 'jotai';
import { companyNameAtom, userRoleAtom } from '../../state/atoms';
import { SendEmailDocument } from '../../queries';
import {
  REQUEST_TO_JOIN_EMAIL_TEMPLATE_ID,
  SPINS_SUPPORT_EMAIL,
  NEW_USER_REQUEST_ERROR_MESSAGE,
} from '../../utils/constants';
import { FormErrorPanel } from '../form-error-panel/form-error-panel';

interface NewUserWithoutAdminProps {
  roleOptions: OptionV2<string>[];
  initialValues: NewUserFormInput;
}

export const NewUserWithoutAdmin = ({
  roleOptions,
  initialValues,
}: NewUserWithoutAdminProps) => {
  const setSelectedUserRole = useSetAtom(userRoleAtom);
  const companyName = useAtomValue(companyNameAtom);
  const [sendEmail] = useMutation(SendEmailDocument);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string>();

  const handleSubmit = async (values: NewUserFormInput) => {
    logger.info('Sending email to customer service...');
    setError(undefined);

    try {
      const { data } = await sendEmail({
        variables: {
          email: SPINS_SUPPORT_EMAIL,
          emailTemplateId: REQUEST_TO_JOIN_EMAIL_TEMPLATE_ID,
          contactProperties: null,
          customProperties: {
            requester_first_name: values.firstName,
            requester_last_name: values.lastName,
            requester_email: values.workEmail,
            requester_role: values.role,
            requester_company: companyName,
          },
        },
      });
      if (!data?.sendEmail) {
        setError(NEW_USER_REQUEST_ERROR_MESSAGE);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      logger.error(err);
      setError(NEW_USER_REQUEST_ERROR_MESSAGE);
    }
  };
  if (submitted) return <NewUserWithoutAdminSubmitted />;

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ isSubmitting, values }) => {
        const allFieldsFilled = Object.values(values).every(
          (value) => value.trim() !== ''
        );

        return (
          <Form>
            <Row>
              <Col>
                <p className={'header'}>Ask the SPINS team for an invite</p>
                <p className={'subtext'}>
                  We found that the company you're a part of is already in our
                  system. We need one of one of our team members to review your
                  request before sending an invite and granting access.
                </p>
              </Col>
            </Row>
            <Row>
              <Col>
                <TextField
                  label="First Name*"
                  name="firstName"
                  onKeyUp={() => undefined}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextField
                  label="Last Name*"
                  name="lastName"
                  autoFocus={false}
                  onKeyUp={() => undefined}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextField
                  label="Work Email"
                  name="workEmail"
                  disabled
                  autoFocus={false}
                  onKeyUp={() => undefined}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BForm.Group controlId="role">
                  <BForm.Label column={'sm'} className={styles['label']}>
                    Role
                  </BForm.Label>
                  <Field name="role">
                    {({ field, form }: FieldProps<string>) => (
                      <DropdownV2.Dropdown
                        id="role"
                        name="role"
                        options={roleOptions}
                        value={field.value}
                        onChange={(value) => {
                          form.setFieldValue('role', value);
                          setSelectedUserRole(value);
                        }}
                        onBlur={() => form.setFieldTouched('role', true)}
                        placeholderText="Select a role"
                      />
                    )}
                  </Field>
                </BForm.Group>
              </Col>
            </Row>
            <FormErrorPanel message={error} />
            <Row className={'form-button-row'}>
              <Col>
                <Button
                  type={'submit'}
                  disabled={isSubmitting || !allFieldsFilled}
                  color="primary"
                  style={{ width: '100%' }}
                >
                  Request to join
                </Button>
              </Col>
            </Row>
            <Row className={'pt-2'}>
              <Col className={styles['sign-in-row']}>
                Having issues?{' '}
                <a href={`/authorize`}>Contact Customer Support</a>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};
